import { defineComponent, inject, computed } from 'vue-demi';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { openBlock, createElementBlock, normalizeClass, createElementVNode, mergeProps, renderSlot, createTextVNode, toDisplayString, pushScopeId, popScopeId } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

var script = defineComponent({
  name: 'OrRadio',
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: null,
      default: null
    },
    value: {
      type: null,
      default: null,
      required: true
    },
    label: {
      type: String,
      default: '',
      required: false
    },
    isDisabled: {
      type: Boolean,
      default: false,
      required: false
    },
    isUncheckable: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, {
    emit
  }) {
    const radioGroupInstance = inject('radioGroupInstance', null);
    const hasInjection = computed(() => !!radioGroupInstance);
    const modifiedIsDisabled = computed(() => {
      return hasInjection.value ? (radioGroupInstance === null || radioGroupInstance === void 0 ? void 0 : radioGroupInstance.props.isDisabled) || props.isDisabled : props.isDisabled;
    });
    const modifiedIsUncheckable = computed(() => {
      return hasInjection.value ? (radioGroupInstance === null || radioGroupInstance === void 0 ? void 0 : radioGroupInstance.props.isUncheckable) || props.isUncheckable : props.isUncheckable;
    });
    const isChecked = computed(() => {
      var _a;
      if (hasInjection.value) {
        return isEqual(get(radioGroupInstance, ['props', 'modelValue'], null), props.value) || isEqual(get(radioGroupInstance, ['props', 'modelValue', 'value'], null), props.value);
      }
      return isEqual(props.modelValue, props.value) || isEqual((_a = props.modelValue) === null || _a === void 0 ? void 0 : _a.value, props.value);
    });
    function onClick() {
      if (!modifiedIsUncheckable.value && isChecked.value || modifiedIsDisabled.value) return;
      const emitValue = modifiedIsUncheckable.value && isChecked.value ? null : {
        label: props.label,
        value: props.value
      };
      hasInjection.value ? radioGroupInstance === null || radioGroupInstance === void 0 ? void 0 : radioGroupInstance.emit('update:modelValue', emitValue) : emit('update:modelValue', emitValue);
    }
    return {
      modifiedIsDisabled,
      modifiedIsUncheckable,
      isChecked,
      onClick
    };
  },
  computed: {
    rootClasses() {
      return ['or-radio', {
        'is-checked': this.isChecked,
        'is-uncheckable': !this.modifiedIsUncheckable,
        'is-disabled': this.modifiedIsDisabled
      }, this.$attrs.class];
    },
    localAttrs() {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
      const {
        class: _,
        ...rest
      } = this.$attrs;
      return rest;
    }
  }
});

const _withScopeId = n => (pushScopeId("data-v-5889a64a"), n = n(), popScopeId(), n);
const _hoisted_1 = ["disabled", "value", "checked"];
const _hoisted_2 = /*#__PURE__*/_withScopeId(() => /*#__PURE__*/createElementVNode("span", {
  class: "or-radio--radio"
}, null, -1 /* HOISTED */));
const _hoisted_3 = {
  class: "or-radio--content"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("label", {
    class: normalizeClass(_ctx.rootClasses)
  }, [createElementVNode("input", mergeProps(_ctx.localAttrs, {
    disabled: _ctx.modifiedIsDisabled,
    value: _ctx.value,
    checked: _ctx.isChecked,
    class: "or-radio--input visually-hidden",
    type: "radio",
    onClick: _cache[0] || (_cache[0] = (...args) => _ctx.onClick && _ctx.onClick(...args))
  }), null, 16 /* FULL_PROPS */, _hoisted_1), _hoisted_2, createElementVNode("span", _hoisted_3, [renderSlot(_ctx.$slots, "default", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])])], 2 /* CLASS */);
}

var css_248z = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-5889a64a {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-5889a64a {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.or-radio[data-v-5889a64a] {\n  display: inline-flex;\n  align-items: center;\n  cursor: pointer;\n}\n.or-radio--radio[data-v-5889a64a] {\n  position: relative;\n  width: 1rem;\n  min-width: 1rem;\n  height: 1rem;\n  min-height: 1rem;\n  margin-right: var(--s-3);\n  background-color: var(--c-white);\n  border: 1px solid var(--c-neutral-2);\n  border-radius: 9999px;\n  transition-timing-function: var(--t-func);\n  transition-duration: var(--t-duration);\n  transition-property: border-color, background-color, opacity;\n}\n.or-radio--radio[data-v-5889a64a]::after {\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  width: 0.25rem;\n  height: 0.25rem;\n  content: \"\";\n  background-color: var(--c-white);\n  border-radius: inherit;\n  transition-delay: 0.15s;\n  transform: translate(-50%, -50%) scale(0);\n  transition-timing-function: var(--t-func);\n  transition-duration: var(--t-duration);\n  transition-property: transform;\n}\n.or-radio--content[data-v-5889a64a] {\n  color: var(--c-neutral-6);\n  transition-timing-function: var(--t-func);\n  transition-duration: var(--t-duration);\n  transition-property: color;\n}\n.or-radio.is-checked.is-uncheckable[data-v-5889a64a] {\n  cursor: default;\n}\n.or-radio.is-checked .or-radio--radio[data-v-5889a64a] {\n  background-color: var(--c-primary);\n  border-color: var(--c-primary);\n}\n.or-radio.is-checked .or-radio--radio[data-v-5889a64a]::after {\n  transform: translate(-50%, -50%) scale(1);\n}\n@media (hover: hover) and (pointer: fine) {\n.or-radio:not(.or-radio.is-loading):not(.or-radio.is-disabled):not(.or-radio.is-readonly):not(.or-radio:disabled):not(.or-radio.is-focused):not(.or-radio:active):hover:not(.is-checked) .or-radio--radio[data-v-5889a64a] {\n    background-color: var(--c-white-darken-3);\n    border-color: var(--c-neutral-2-darken-3);\n}\n}\n@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {\n.or-radio:not(.or-radio.is-loading):not(.or-radio.is-disabled):not(.or-radio.is-readonly):not(.or-radio:disabled):not(.or-radio.is-focused):not(.or-radio:active):hover:not(.is-checked) .or-radio--radio[data-v-5889a64a] {\n    background-color: var(--c-white-darken-3);\n    border-color: var(--c-neutral-2-darken-3);\n}\n}\n.or-radio:not(:disabled):not(.is-loading):not(.is-disabled):not(.is-readonly):active:not(.is-checked) .or-radio--radio[data-v-5889a64a] {\n  background-color: var(--c-white-darken-5);\n  border-color: var(--c-neutral-2-darken-5);\n}\n.or-radio[data-v-5889a64a]:disabled, .or-radio.is-disabled[data-v-5889a64a] {\n  cursor: not-allowed;\n}\n.or-radio:disabled .or-radio--radio[data-v-5889a64a], .or-radio.is-disabled .or-radio--radio[data-v-5889a64a] {\n  opacity: 0.5;\n}\n.or-radio:disabled .or-radio--content[data-v-5889a64a], .or-radio.is-disabled .or-radio--content[data-v-5889a64a] {\n  color: var(--c-neutral-4);\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-5889a64a";
script.__file = "src/components/or-radio/OrRadio.vue";

export { script as s };
